// WorkspaceAddModal.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useProgress } from 'context/ProgressContext';
import { useUser } from 'context/UserContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './WorkspaceAddModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

// Services
import { createWorkspace } from 'services/workspaces/WorkspaceWriteService';

/**
 * WorkspaceAddModal
 * 
 * Modal for adding a new workspace.
 * 
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onSave - Callback when title is saved.
 * 
 * @returns {ReactNode} - WorkspaceAddModal component.
 */
const WorkspaceAddModal = ({ isOpen, setOpen, onSave }) => {

    // ------ STATE ------ //

    // State
    const [title, setTitle] = useState("");

    // ------ HOOKS ------ //

    // Context
    const { showProgress, hideProgress } = useProgress();
    const { user } = useUser();

    // Device Detection
    const isMobile = useIsMobile();

    // Reset title when modal opens
    useEffect(() => {
        if (isOpen) {
            setTitle("");
        }
    }, [isOpen]);

    // ------ HANDLERS ------ //

    /**
     * Handles saving the title.
     */
    const handleSave = async () => {
        const trimmedTitle = title.trim();
        if (!trimmedTitle) return;
        if (!user?.key) return;

        try {
            showProgress && showProgress('Creating space...');
            const created = await createWorkspace(user.key, trimmedTitle, false, user);
            if (!created?.key) return;

            onSave && onSave(created);
            setOpen(false);
        } catch (error) {
            console.error('[WorkspaceAddModal] create space error:', error);
        } finally {
            hideProgress && hideProgress();
        }
    };

    // ------ RENDER ------ //

    return (
        <Modal
            iconKey="workspaces"
            title="Add Space"
            isOpen={isOpen}
            onClose={() => setOpen(false)}
            width={isMobile ? '100%' : 460}
            height="auto"
        >
            <div className="workspace-add-modal-container">

                {/* TITLE */}
                <ModalLabel label="Title" />
                <TextInput
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={100}
                    autoFocus
                    autoSelect
                    showCount
                />

            </div>

            {/* BUTTONS */}
            <ModalButtons>
                <Button onClick={handleSave} label="Save" />
            </ModalButtons>

        </Modal>
    );
};

export default WorkspaceAddModal;
